// frontend/src/components/CampaignManager/dialogs/LotteryDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Lottery } from '../types';

interface Props {
  open: boolean;
  lottery: Lottery | null;
  onClose: () => void;
  onSave: (lottery: Partial<Lottery>, id?: number) => Promise<void>;
}

export default function LotteryDialog({ open, lottery, onClose, onSave }: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [form, setForm] = useState({
    name: '',
    name_kurz: '',
    type: 'SKL' as 'SKL' | 'NKL',
    startdatum: ''
  });

  const [saving, setSaving] = useState(false);

  // Helper function to extract lottery number from name
  const extractLotteryNumber = (name: string): string => {
    // Try multiple patterns to extract the lottery number
    // Pattern 1: "Lotterie 123" or "123. Lotterie"
    let match = name.match(/Lotterie\s+(\d+)/i);
    if (match) return match[1];

    match = name.match(/(\d+)\.\s*Lotterie/i);
    if (match) return match[1];

    // Pattern 2: Just a number at the start or end
    match = name.match(/^(\d+)/);
    if (match) return match[1];

    match = name.match(/(\d+)$/);
    if (match) return match[1];

    // Pattern 3: Any number in the string
    match = name.match(/(\d+)/);
    if (match) return match[1];

    return '';
  };

  // Update form when lottery changes
  useEffect(() => {
    if (lottery) {
      setForm({
        name: lottery.name,
        name_kurz: lottery.name_kurz,
        type: lottery.type,
        startdatum: lottery.startdatum.split('T')[0]
      });
    } else {
      // Reset form for new lottery
      setForm({
        name: '',
        name_kurz: '',
        type: 'SKL',
        startdatum: ''
      });
    }
  }, [lottery, open]);

  // Auto-fill name_kurz when name changes
  const handleNameChange = (newName: string) => {
    const lotterieNumber = extractLotteryNumber(newName);
    setForm({
      ...form,
      name: newName,
      name_kurz: lotterieNumber || form.name_kurz
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form, lottery?.id);
      onClose();
    } catch (error) {
      console.error('Error saving lottery:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        {lottery ? 'Lotterie bearbeiten' : 'Neue Lotterie'}
      </DialogTitle>
      
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="Name"
            value={form.name}
            onChange={(e) => handleNameChange(e.target.value)}
            disabled={saving}
            required
            placeholder="Lotterie 123"
            helperText="Format: Lotterie [Lotterienummer], z.B. 'Lotterie 123'"
          />

          <TextField
            fullWidth
            label="Kurzname"
            value={form.name_kurz}
            onChange={(e) => setForm({ ...form, name_kurz: e.target.value })}
            disabled={saving}
            required
            helperText="Wird automatisch aus dem Namen übernommen, kann aber manuell angepasst werden"
          />

          <TextField
            fullWidth
            type="date"
            label="Startdatum"
            value={form.startdatum}
            onChange={(e) => setForm({ ...form, startdatum: e.target.value })}
            InputLabelProps={{ shrink: true }}
            disabled={saving}
            required
            helperText="Das Jahr wird automatisch aus dem Startdatum übernommen"
          />

          <FormControl fullWidth required>
            <InputLabel>Typ</InputLabel>
            <Select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value as 'SKL' | 'NKL' })}
              label="Typ"
              disabled={saving}
            >
              <MenuItem value="SKL">SKL</MenuItem>
              <MenuItem value="NKL">NKL</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Abbrechen
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{ backgroundColor: '#003366' }}
          disabled={saving || !form.name || !form.name_kurz || !form.startdatum}
        >
          {saving ? 'Speichern...' : 'Speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
